"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TreeNode = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const index_js_1 = require("../utils/index.js");
require("@itwin/itwinui-css/css/tree.css");
const classnames_1 = __importDefault(require("classnames"));
const TreeNodeExpander_js_1 = require("./TreeNodeExpander.js");
const TreeContext_js_1 = require("./TreeContext.js");
/**
 * `TreeNode` component to display node content within a `Tree`.
 * Must be used inside `Tree` component to correctly set node `depth` and `subNodes`.
 * @example
  <TreeNode
    nodeId={props.nodeId}
    label={props.node.label}
    sublabel={props.node.sublabel}
    onExpanded={onExpanded}
    onSelected={onSelectedNodeChange}
    isDisabled={props.isDisabled}
    isExpanded={props.isExpanded}
    isSelected={props.isSelected}
    checkbox={
      <Checkbox variant='eyeball' disabled={props.isDisabled} />
    }
    icon={<SvgPlaceholder />}
  />
 */
const TreeNode = (props) => {
    const { nodeId, label, sublabel, children, className, icon, hasSubNodes = false, isDisabled = false, isExpanded = false, isSelected = false, onSelected, onExpanded, checkbox, expander, ...rest } = props;
    (0, index_js_1.useTheme)();
    const { nodeDepth, subNodeIds = [], parentNodeId, scrollToParent, groupSize, indexInGroup, } = (0, TreeContext_js_1.useTreeContext)();
    const [isFocused, setIsFocused] = React.useState(false);
    const nodeRef = React.useRef(null);
    const onKeyDown = (event) => {
        var _a, _b, _c, _d, _e, _f;
        if (event.altKey) {
            return;
        }
        const isNodeFocused = nodeRef.current === ((_a = nodeRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.activeElement);
        switch (event.key) {
            case 'ArrowLeft': {
                event.preventDefault();
                if (isNodeFocused) {
                    if (isExpanded) {
                        onExpanded(nodeId, false);
                        break;
                    }
                    if (parentNodeId) {
                        scrollToParent === null || scrollToParent === void 0 ? void 0 : scrollToParent();
                        break;
                    }
                    // If it is top level node (doesn't have parent node), then do nothing.
                    break;
                }
                const focusableElements = (0, index_js_1.getFocusableElements)(nodeRef.current);
                const currentIndex = focusableElements.indexOf((_b = nodeRef.current) === null || _b === void 0 ? void 0 : _b.ownerDocument.activeElement);
                if (currentIndex === 0) {
                    (_c = nodeRef.current) === null || _c === void 0 ? void 0 : _c.focus();
                }
                else {
                    (_d = focusableElements[currentIndex - 1]) === null || _d === void 0 ? void 0 : _d.focus();
                }
                break;
            }
            case 'ArrowRight': {
                event.preventDefault();
                const focusableElements = (0, index_js_1.getFocusableElements)(nodeRef.current);
                if (isNodeFocused) {
                    if (!isExpanded && hasSubNodes) {
                        onExpanded(nodeId, true);
                        break;
                    }
                    (_e = focusableElements[0]) === null || _e === void 0 ? void 0 : _e.focus();
                    break;
                }
                const currentIndex = focusableElements.indexOf((_f = nodeRef.current) === null || _f === void 0 ? void 0 : _f.ownerDocument.activeElement);
                if (currentIndex < focusableElements.length - 1) {
                    focusableElements[currentIndex + 1].focus();
                    break;
                }
                break;
            }
            case ' ':
            case 'Spacebar':
            case 'Enter': {
                // Ignore if it is called on the element inside, e.g. checkbox or expander
                if (event.target !== nodeRef.current) {
                    break;
                }
                event.preventDefault();
                if (!isDisabled) {
                    onSelected === null || onSelected === void 0 ? void 0 : onSelected(nodeId, !isSelected);
                }
                break;
            }
            default:
                break;
        }
    };
    const onExpanderClick = React.useCallback((event) => {
        onExpanded(nodeId, !isExpanded);
        event.stopPropagation();
    }, [isExpanded, nodeId, onExpanded]);
    return (React.createElement("li", { role: 'treeitem', className: (0, classnames_1.default)('iui-tree-item', className), id: nodeId, "aria-expanded": hasSubNodes ? isExpanded : undefined, "aria-disabled": isDisabled, "aria-selected": isSelected, "aria-level": nodeDepth + 1, "aria-setsize": groupSize, "aria-posinset": indexInGroup + 1, tabIndex: -1, onFocus: (e) => {
            setIsFocused(true);
            // Prevents from triggering onFocus on parent Tree
            e.stopPropagation();
        }, onBlur: () => {
            setIsFocused(false);
        }, ref: nodeRef, onKeyDown: onKeyDown, ...rest },
        React.createElement("div", { className: (0, classnames_1.default)('iui-tree-node', {
                'iui-active': isSelected,
                'iui-disabled': isDisabled,
            }), style: { '--level': nodeDepth }, onClick: () => !isDisabled && (onSelected === null || onSelected === void 0 ? void 0 : onSelected(nodeId, !isSelected)) },
            checkbox && React.isValidElement(checkbox)
                ? React.cloneElement(checkbox, {
                    className: (0, classnames_1.default)('iui-tree-node-checkbox', checkbox.props.className),
                    tabIndex: isFocused ? 0 : -1,
                })
                : checkbox,
            React.createElement("div", { className: 'iui-tree-node-content' },
                hasSubNodes && expander,
                hasSubNodes && !expander && (React.createElement(TreeNodeExpander_js_1.TreeNodeExpander, { isExpanded: isExpanded, disabled: isDisabled, onClick: onExpanderClick, tabIndex: isFocused ? 0 : -1 })),
                icon &&
                    React.cloneElement(icon, {
                        className: (0, classnames_1.default)('iui-tree-node-content-icon', icon.props.className),
                    }),
                React.createElement("span", { className: 'iui-tree-node-content-label' },
                    React.createElement("div", { className: 'iui-tree-node-content-title' }, label),
                    sublabel && (React.createElement("div", { className: 'iui-tree-node-content-caption' }, sublabel))),
                children)),
        hasSubNodes && (React.createElement("ul", { className: 'iui-sub-tree', role: 'group', "aria-owns": subNodeIds.join(',') }))));
};
exports.TreeNode = TreeNode;
exports.default = exports.TreeNode;
